import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, mergeProps as _mergeProps, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { ref, onMounted, watch, computed } from "vue";
import { GraphMode } from "@/types";
import {
  appStore,
  artifactStore,
  deltaStore,
  layoutStore,
  permissionStore,
  useScreen,
} from "@/hooks";
import { TextButton } from "@/components/common";
import DeltaModeButton from "./DeltaModeButton.vue";


/**
 * Buttons for changing the mode of the artifact view.
 */
const __default__ = {
  name: "ModeButtons",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const { smallWindow } = useScreen();

const value = ref<GraphMode[]>([]);

const treeTooltip = computed(() => {
  return artifactStore.largeNodeCount
    ? "The tree view is disabled for very large graphs. " +
        'To view a tree, visit "Table" view and select the tree icon next to an artifact, or create a new custom "View" above.'
    : "A graph of related artifacts";
});

const hasArtifacts = computed(() => artifactStore.allArtifacts.length > 0);

/**
 * Returns props for a mode button.
 * @param option - The mode button to get props for.
 */
function buttonProps(option: GraphMode) {
  const selected = value.value.includes(option);

  return {
    text: !selected,
    color: selected ? undefined : "text",
    class: selected ? "button-group-selected text-primary" : "",
  };
}

/**
 * Updates the values of which buttons are highlighted.
 */
function updateValue(): void {
  const selected: GraphMode[] = [];

  selected.push(layoutStore.mode);

  if (deltaStore.inDeltaView) {
    selected.push("delta");
  }

  value.value = selected;
}

/**
 * Opens tree view.
 */
function handleTimView(): void {
  layoutStore.mode = "tim";
}

/**
 * Opens tree view.
 */
function handleTreeView(): void {
  layoutStore.mode = "tree";

  appStore.closeSidePanels();
}

/**
 * Opens table view.
 */
function handleTableView(): void {
  layoutStore.mode = "table";

  appStore.closeSidePanels();
}
/**
 * Opens delta view.
 */
function handleDeltaView(): void {
  if (!deltaStore.inDeltaView) return;

  appStore.openDetailsPanel("delta");
  updateValue();
}

/**
 * Opens chat view.
 */
function handleChatView(): void {
  layoutStore.mode = "chat";

  appStore.closeSidePanels();
}

onMounted(() => updateValue());

watch(
  () => layoutStore.mode,
  () => updateValue()
);

watch(
  () => deltaStore.inDeltaView,
  () => updateValue()
);

return (_ctx: any,_cache: any) => {
  const _component_q_tooltip = _resolveComponent("q-tooltip")!
  const _component_q_btn_group = _resolveComponent("q-btn-group")!

  return (_openBlock(), _createBlock(_component_q_btn_group, {
    flat: "",
    class: "q-mx-sm nav-mode-select"
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(TextButton), _mergeProps(buttonProps('tim'), {
        "hide-label": _unref(smallWindow),
        label: "Types",
        "data-cy": "button-nav-tim",
        icon: "view-tim",
        onClick: handleTimView
      }), {
        default: _withCtx(() => [
          _createVNode(_component_q_tooltip, null, {
            default: _withCtx(() => [
              _createTextVNode(" The types of artifacts and how they relate ")
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 16, ["hide-label"]),
      _createVNode(_unref(TextButton), _mergeProps(buttonProps('tree'), {
        "hide-label": _unref(smallWindow),
        disabled: _unref(artifactStore).largeNodeCount,
        label: "Tree",
        "data-cy": "button-nav-tree",
        icon: "view-tree",
        onClick: handleTreeView
      }), {
        default: _withCtx(() => [
          _createVNode(_component_q_tooltip, null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(treeTooltip.value), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 16, ["hide-label", "disabled"]),
      _createVNode(_unref(TextButton), _mergeProps(buttonProps('table'), {
        "hide-label": _unref(smallWindow),
        disabled: !hasArtifacts.value,
        label: "Table",
        "data-cy": "button-nav-table",
        icon: "view-table",
        onClick: handleTableView
      }), {
        default: _withCtx(() => [
          _createVNode(_component_q_tooltip, null, {
            default: _withCtx(() => [
              _createTextVNode(" Tables of artifacts and trace links ")
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 16, ["hide-label", "disabled"]),
      _createVNode(DeltaModeButton, _mergeProps(buttonProps('delta'), {
        "hide-label": _unref(smallWindow),
        onClick: handleDeltaView
      }), null, 16, ["hide-label"]),
      (_unref(permissionStore).isNASA)
        ? (_openBlock(), _createBlock(_unref(TextButton), _mergeProps({ key: 0 }, buttonProps('chat'), {
            "hide-label": _unref(smallWindow),
            disabled: !hasArtifacts.value,
            label: "Chat",
            "data-cy": "button-nav-chat",
            icon: "comment",
            onClick: handleChatView
          }), {
            default: _withCtx(() => [
              _createVNode(_component_q_tooltip, null, {
                default: _withCtx(() => [
                  _createTextVNode(" Chat with your project data ")
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 16, ["hide-label", "disabled"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})